import { forwardRef } from "react";
import { fmt, pct, height, positionsOf } from "./kit";
import { ShotZones } from "./ShotZones";

const COMBINE = [["standing_vert_in", "Standing vert", "in"], ["running_vert_in", "Running vert", "in"], ["wingspan_in", "Wingspan", "in"], ["lane_agility_s", "Lane agility", "s"], ["three_quarter_sprint_s", "3/4 sprint", "s"], ["bench_reps", "Bench", ""]];

export const DEFAULT_BRAND = { accent: "#39FF14", background: "#0A0E1A", logo: null, school_name: "" };

const Big = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-6 py-5">
    <p className="font-mono-gd text-[15px] uppercase tracking-[0.25em] text-slate-300">{label}</p>
    <p className="font-mono-gd text-6xl font-bold tracking-tighter" style={{ color: accent ?? "#fff" }}>{value}</p>
  </div>
);

export const ProspectCard = forwardRef(({ data, org, brand }, ref) => {
  const b = { ...DEFAULT_BRAND, ...(brand ?? {}) };
  const A = b.accent;
  const p = data?.player ?? {};
  const s = data?.season ?? {};
  const best = data?.personal_bests ?? {};
  const latest = data?.latest_combine ?? {};
  const cmp = data?.comparison?.metrics ?? {};
  const combine = COMBINE.filter(([k]) => (latest[k] ?? best[k]) != null).slice(0, 4);
  const scout = b.school_name || org || "GAMEDAY recruiter";
  const reel = (Array.isArray(data?.highlight_links) ? data.highlight_links : Array.isArray(p.highlight_links) ? p.highlight_links : [])
    .map((l) => (typeof l === "string" ? l : l?.url)).find(Boolean);
  return (
    <div ref={ref} data-testid="prospect-card" className="relative flex h-[1350px] w-[1080px] flex-col overflow-hidden p-16 text-white" style={{ fontFamily: "Archivo, sans-serif", backgroundColor: b.background }}>
      <div className="absolute -right-48 -top-48 h-[640px] w-[640px] rounded-full opacity-20 blur-[120px]" style={{ backgroundColor: A }} />
      <div className="absolute inset-x-0 bottom-0 h-3" style={{ backgroundColor: A }} />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/logo-mark.png" alt="" className="h-16 w-auto" />
          <div className="leading-none">
            <p className="font-display text-4xl font-extrabold">GAME<span className="text-[#FF3B30]">DAY</span></p>
            <p className="font-mono-gd text-sm uppercase tracking-[0.35em]" style={{ color: A }}>Prospect card</p>
          </div>
        </div>
        <div className="flex items-center gap-5">
          {data?.tag && <span className="rounded-full px-6 py-3 font-mono-gd text-lg font-bold uppercase tracking-wider" style={{ backgroundColor: A, color: b.background }}>{data.tag}</span>}
          {b.logo && <img src={b.logo} alt={scout} data-testid="card-school-logo" className="h-24 w-24 rounded-2xl bg-white/5 object-contain p-2" />}
        </div>
      </div>
      <div className="relative mt-14">
        <p className="font-mono-gd text-xl uppercase tracking-[0.3em]" style={{ color: A }}>#{p.jersey ?? "–"} · {positionsOf(p)} · Class of {p.grad_year ?? "—"}</p>
        <h1 className="mt-3 font-display text-[8.5rem] font-black uppercase leading-[0.85]">{p.name ?? "Prospect"}</h1>
        <p className="mt-4 text-2xl text-slate-300">{data?.team_name ?? ""} · {height(p.height_in)}{p.weight_lb ? ` · ${p.weight_lb} lb` : ""}{p.gpa != null ? ` · GPA ${fmt(p.gpa)}` : ""}</p>
        {p.primary_archetype && (
          <div data-testid="card-archetypes" className="mt-6 flex flex-wrap gap-3">
            <span className="rounded-full px-5 py-2 font-mono-gd text-lg font-bold uppercase tracking-wider" style={{ backgroundColor: A, color: b.background }}>{p.primary_archetype}</span>
            {p.secondary_archetype && <span className="rounded-full border px-5 py-2 font-mono-gd text-lg uppercase tracking-wider text-white" style={{ borderColor: `${A}88` }}>{p.secondary_archetype}</span>}
          </div>
        )}
      </div>
      <div className="relative mt-12 grid grid-cols-4 gap-4">
        <Big label="PPG" value={fmt(s.ppg)} accent={A} /><Big label="RPG" value={fmt(s.rpg)} /><Big label="APG" value={fmt(s.apg)} /><Big label="TS%" value={s.ts_pct == null ? "—" : fmt(s.ts_pct, 0)} />
      </div>
      <div className="relative mt-10 grid flex-1 grid-cols-2 gap-10">
        <div>
          <p className="mb-4 font-mono-gd text-base uppercase tracking-[0.25em] text-slate-300">Shot zones · {fmt(s.gp)} GP</p>
          <ShotZones zones={data?.zone_pct ?? {}} testid="card-zones" />
          <div className="mt-6 grid grid-cols-3 gap-3 font-mono-gd text-lg">
            <p><span className="text-slate-300">FG </span>{pct(s.fg_pct)}</p><p><span className="text-slate-300">3P </span>{pct(s.fg3_pct)}</p><p><span className="text-slate-300">FT </span>{pct(s.ft_pct)}</p>
          </div>
        </div>
        <div>
          <p className="mb-4 font-mono-gd text-base uppercase tracking-[0.25em] text-slate-300">Combine vs. NBA Draft Combine avg{data?.comparison?.label ? ` (drafted ${data.comparison.label}s)` : ""}</p>
          {combine.length ? (
            <ul className="space-y-3">
              {combine.map(([k, l, u]) => (
                <li key={k} className="flex items-baseline justify-between rounded-2xl bg-white/[0.05] px-6 py-4">
                  <span className="text-xl text-slate-200">{l}</span>
                  <span className="font-mono-gd text-3xl font-bold text-white">{fmt(latest[k] ?? best[k])}{u}<span className="ml-3 text-lg text-slate-300">/ {fmt(cmp[k])}{u}</span></span>
                </li>
              ))}
            </ul>
          ) : <p className="rounded-2xl border border-dashed border-white/15 p-6 text-xl text-slate-300">No combine testing logged yet.</p>}
        </div>
      </div>
      {reel && (
        <div data-testid="card-highlights" className="relative mt-8 flex items-center gap-5 rounded-2xl border px-6 py-4" style={{ borderColor: `${A}66` }}>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: A }}>
            <span className="ml-1 h-0 w-0 border-y-[11px] border-l-[18px] border-y-transparent" style={{ borderLeftColor: b.background }} />
          </span>
          <div className="min-w-0">
            <p className="font-mono-gd text-base uppercase tracking-[0.25em]" style={{ color: A }}>Watch highlights</p>
            <p className="truncate font-mono-gd text-xl text-white">{reel.replace(/^https?:\/\//, "")}</p>
          </div>
        </div>
      )}
      <div className="relative mt-8 flex items-center justify-between border-t border-white/10 pt-6 font-mono-gd text-base uppercase tracking-[0.2em] text-slate-300">
        <span>Scouted by <span style={{ color: A }}>{scout}</span></span>
        <span>{new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
      </div>
    </div>
  );
});
ProspectCard.displayName = "ProspectCard";
